import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Toast } from 'src/components/Toast';
import { UserRoles } from 'src/constants/userConsts';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetPortalConfigQuery } from 'src/services/api';
import {
  useGetDismissedLatestReleaseQuery,
  useUpdateLatestReleaseDismissalMutation,
} from 'src/services/api/LatestReleaseApi';
import { useGetWhatsNewAnnouncementsQuery } from 'src/services/api/whatsNewApi';
import { selectCurrentUser } from 'src/store/storeUtils';
import { AlertVariant } from 'src/store/ui/types';

import {
  selectWhatsNewAnnouncement,
  shouldShowWhatsNew,
} from './selectWhatsNewAnnouncement';
import { WhatsNewModal } from './WhatsNewModal';

/**
 * `?showLatestRelease=true` forces the modal open (ignoring dismissal and
 * workspace age) and `=hide` forces it closed, which automated test
 * recordings rely on.
 */
const SHOW_OVERRIDE_PARAM = 'showLatestRelease';

const getShowOverride = () =>
  new URLSearchParams(window.location.search).get(SHOW_OVERRIDE_PARAM);

const openLink = (url: string, navigate: (path: string) => void) => {
  if (url.startsWith('/')) {
    navigate(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const WhatsNew = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isClient = useAppSelector((state) => state.user.isClient);
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin =
    currentUser?.fields?.roles?.includes(UserRoles.InternalAdmin) ?? false;

  const showOverride = getShowOverride();
  const skip = isClient || showOverride === 'hide';

  const { data: portalConfig } = useGetPortalConfigQuery(undefined, { skip });
  const isForcedOpen = showOverride === 'true';
  const { data: announcements = [] } = useGetWhatsNewAnnouncementsQuery(
    undefined,
    { skip },
  );
  const { data: dismissal, isLoading: isLoadingDismissal } =
    useGetDismissedLatestReleaseQuery(null, { skip });
  const [updateDismissal] = useUpdateLatestReleaseDismissalMutation();
  // Force-open ignores stored dismissal, so track this session's close
  // separately. A normal refresh clears this and re-reads the BE record —
  // dismissed stays hidden; not-yet-dismissed shows again.
  const [isClosed, setIsClosed] = useState(false);

  const announcement = useMemo(
    () =>
      selectWhatsNewAnnouncement({
        announcements,
        isAdmin,
      }),
    [announcements, isAdmin],
  );

  const handleUndoDismiss = useCallback(() => {
    void updateDismissal({
      structFields: { latest_release_dismissed: '' },
    });
    setIsClosed(false);
  }, [updateDismissal]);

  const successDismissSnackbar = useCallback(
    (snackbarKey: string | number) => (
      <div key={snackbarKey}>
        <Toast
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(snackbarKey)}
          description={t(($) => $.components.whats_new.release_closed)}
          actionProps={{
            onClick: handleUndoDismiss,
            children: t(($) => $.components.whats_new.undo),
            className: 'text-base',
          }}
        />
      </div>
    ),
    [closeSnackbar, handleUndoDismiss, t],
  );

  const handleDismiss = useCallback(async () => {
    if (!announcement) return;
    setIsClosed(true);
    const result = await updateDismissal({
      structFields: { latest_release_dismissed: announcement.id },
    });
    // The mutation rolls back its optimistic dismissal on failure, so reopen
    // the modal to match instead of hiding an announcement that isn't saved.
    if (!('data' in result)) {
      setIsClosed(false);
      return;
    }
    enqueueSnackbar(
      t(($) => $.components.whats_new.release_closed),
      {
        content: (snackbarKey: string | number) =>
          successDismissSnackbar(snackbarKey),
      },
    );
  }, [
    announcement,
    enqueueSnackbar,
    successDismissSnackbar,
    t,
    updateDismissal,
  ]);

  if (skip || isClosed || !announcement || isLoadingDismissal) return null;

  if (
    !shouldShowWhatsNew({
      announcementId: announcement.id,
      dismissedId: dismissal?.structFields.latest_release_dismissed,
      workspaceCreatedAt: portalConfig?.createdAt,
      isForcedOpen,
    })
  ) {
    return null;
  }

  return (
    <WhatsNewModal
      announcement={announcement}
      onDismiss={() => {
        void handleDismiss();
      }}
      onOpenLink={(url) => openLink(url, navigate)}
    />
  );
};
